
import React, { useState, useMemo } from 'react';
import { MindMapNode, Language } from '../types';
import { X, Search, Link as LinkIcon, Type, Image as ImageIcon, ChevronRight, CornerUpLeft } from 'lucide-react';

interface LinkSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  nodes: MindMapNode[];
  onSelect: (targetId: string) => void;
  sourceId: string;
  language: Language;
}

export const LinkSelectorModal: React.FC<LinkSelectorModalProps> = ({
  isOpen,
  onClose,
  nodes,
  onSelect,
  sourceId,
  language
}) => {
  const [searchQuery, setSearchQuery] = useState("");

  const filteredNodes = useMemo(() => {
    return nodes.filter(n => {
      if (n.id === sourceId) return false;
      const query = searchQuery.toLowerCase();
      return (
        n.title.toLowerCase().includes(query) ||
        n.content.toLowerCase().includes(query)
      );
    });
  }, [nodes, sourceId, searchQuery]);

  const sourceNode = nodes.find(n => n.id === sourceId);

  if (!isOpen) return null;

  const labels = {
    english: {
      title: "Select Target Note",
      searchPlaceholder: "Search notes...",
      noResults: "No notes found.",
      linkingFrom: "Linking from",
      select: "Select"
    },
    vietnamese: {
      title: "Chọn ghi chú đích",
      searchPlaceholder: "Tìm kiếm ghi chú...",
      noResults: "Không tìm thấy ghi chú nào.",
      linkingFrom: "Liên kết từ",
      select: "Chọn"
    }
  };

  const t = labels[language];

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[250] flex items-center justify-center p-4 animate-in fade-in duration-200">
      <div 
        className="bg-white dark:bg-slate-800 w-full max-w-lg rounded-2xl shadow-2xl border border-gray-100 dark:border-slate-700 flex flex-col overflow-hidden animate-in zoom-in-95 duration-200 max-h-[80vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-5 border-b dark:border-slate-700 flex justify-between items-center bg-gray-50 dark:bg-slate-900 shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 rounded-lg">
              <LinkIcon size={20} />
            </div>
            <div>
              <h3 className="font-bold text-lg text-gray-900 dark:text-white leading-tight">{t.title}</h3>
              <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5 uppercase font-bold tracking-wider">
                {t.linkingFrom}: <span className="text-indigo-600 dark:text-indigo-400">{sourceNode?.title || "Untitled"}</span>
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-200 dark:hover:bg-slate-700 rounded-full transition-colors text-gray-500">
            <X size={20} />
          </button>
        </div>

        {/* Search Bar */}
        <div className="p-4 border-b dark:border-slate-700 bg-white dark:bg-slate-900 sticky top-0 z-10">
          <div className="relative group">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-indigo-500 transition-colors" size={18} />
            <input 
              autoFocus
              type="text" 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t.searchPlaceholder}
              className="w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-slate-950 border border-gray-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all dark:text-white"
            />
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto p-2 bg-white dark:bg-slate-900 space-y-1">
          {filteredNodes.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <p>{t.noResults}</p>
            </div>
          ) : (
            filteredNodes.map(node => (
              <div 
                key={node.id}
                onClick={() => onSelect(node.id)}
                className="group flex items-center gap-4 p-3 rounded-xl hover:bg-indigo-50 dark:hover:bg-indigo-900/20 border border-transparent hover:border-indigo-100 dark:hover:border-indigo-900/50 cursor-pointer transition-all"
              >
                {node.imageBase64 ? (
                  <div className="w-12 h-12 flex-shrink-0 bg-gray-100 dark:bg-slate-800 rounded-lg overflow-hidden border border-gray-200 dark:border-slate-700">
                    <img src={node.imageBase64} alt="" className="w-full h-full object-cover" />
                  </div>
                ) : (
                  <div className="w-12 h-12 flex-shrink-0 bg-gray-100 dark:bg-slate-800 rounded-lg flex items-center justify-center text-gray-400">
                    <Type size={20} />
                  </div>
                )}

                <div className="flex-1 min-w-0">
                  <h4 className="font-bold text-sm text-gray-900 dark:text-gray-100 truncate group-hover:text-indigo-600 dark:group-hover:text-indigo-400">
                    {node.title || "Untitled Note"}
                  </h4>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                    {node.content || "No content."}
                  </p>
                </div>

                <div className="text-indigo-400 opacity-0 group-hover:opacity-100 transition-opacity pr-2">
                  <ChevronRight size={18} />
                </div>
              </div>
            ))
          )}
        </div>

        <div className="p-4 bg-gray-50 dark:bg-slate-950 border-t dark:border-slate-800 shrink-0">
          <p className="text-[10px] text-gray-400 font-medium text-center italic">
            Tip: Select the note you want to connect to.
          </p>
        </div>
      </div>
    </div>
  );
};
